import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { EmailTemplate, AlertInfo } from '../types';

interface ContactValidationResult {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  variables: Record<string, string>;
  isValid: boolean;
  exists: boolean;
  existingContact?: any;
  issues: string[];
  missingVariables: string[];
}

interface EnhancedCSVReviewProps {
  open: boolean;
  onClose: () => void;
  contacts: ContactValidationResult[];
  template: EmailTemplate | null;
  onContactsConfirmed: (contacts: ContactValidationResult[], updateExisting: boolean) => void;
  showAlert: (message: string, severity: AlertInfo['severity']) => void;
}

const EnhancedCSVReview: React.FC<EnhancedCSVReviewProps> = ({
  open,
  onClose,
  contacts,
  template,
  onContactsConfirmed,
  showAlert,
}) => {
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [editingContact, setEditingContact] = useState<string | null>(null);
  const [editedContacts, setEditedContacts] = useState<Record<string, ContactValidationResult>>({});
  const [updateExisting, setUpdateExisting] = useState(false);
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);

  useEffect(() => {
    // Auto-select valid contacts
    const validEmails = contacts
      .filter(contact => contact.isValid && contact.missingVariables.length === 0)
      .map(contact => contact.email);
    setSelectedContacts(new Set(validEmails));
  }, [contacts]);

  const getWorkingContacts = () => {
    return contacts.map(contact => editedContacts[contact.email] || contact);
  };

  const getFilteredContacts = () => {
    const workingContacts = getWorkingContacts();
    if (showOnlyIssues) {
      return workingContacts.filter(contact => 
        !contact.isValid || contact.missingVariables.length > 0 || contact.exists
      );
    }
    return workingContacts;
  };

  const handleContactToggle = (email: string) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(email)) {
      newSelected.delete(email);
    } else {
      newSelected.add(email);
    }
    setSelectedContacts(newSelected);
  };

  const handleSelectAll = () => {
    const filteredContacts = getFilteredContacts();
    const validContacts = filteredContacts.filter(contact => 
      contact.isValid && contact.missingVariables.length === 0
    );
    
    if (selectedContacts.size === validContacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(validContacts.map(c => c.email)));
    }
  };

  const handleEditContact = (contact: ContactValidationResult) => {
    setEditingContact(contact.email);
  };

  const handleSaveContact = (email: string) => {
    const contact = editedContacts[email] || contacts.find(c => c.email === email);
    if (!contact) return;

    // Recalculate missing variables
    const missingVariables = template?.variables.filter(variable => 
      !contact.variables[variable] || contact.variables[variable].trim() === ''
    ) || [];

    const updatedContact = {
      ...contact,
      missingVariables,
      issues: [
        ...(contact.isValid ? [] : ['Invalid email format']),
        ...(contact.exists ? ['Email already exists'] : []),
        ...missingVariables.map(v => `Missing variable: ${v}`)
      ]
    };

    setEditedContacts(prev => ({
      ...prev,
      [email]: updatedContact
    }));

    setEditingContact(null);
    
    // Auto-select if now valid
    if (updatedContact.isValid && updatedContact.missingVariables.length === 0) {
      setSelectedContacts(prev => new Set([...Array.from(prev), email]));
    }
  };

  const handleCancelEdit = () => {
    setEditingContact(null);
  };

  const handleVariableChange = (email: string, variable: string, value: string) => {
    const contact = editedContacts[email] || contacts.find(c => c.email === email);
    if (!contact) return;

    const updatedContact = {
      ...contact,
      variables: {
        ...contact.variables,
        [variable]: value
      }
    };

    setEditedContacts(prev => ({
      ...prev,
      [email]: updatedContact
    }));
  };

  const handleFieldChange = (email: string, field: string, value: string) => {
    const contact = editedContacts[email] || contacts.find(c => c.email === email);
    if (!contact) return;

    const updatedContact = {
      ...contact,
      [field]: value
    };

    setEditedContacts(prev => ({
      ...prev,
      [email]: updatedContact
    }));
  };

  const handleConfirm = () => {
    const workingContacts = getWorkingContacts();
    const confirmedContacts = workingContacts.filter(contact => 
      selectedContacts.has(contact.email)
    );
    
    onContactsConfirmed(confirmedContacts, updateExisting);
    onClose();
  };

  const handleCancel = () => {
    setSelectedContacts(new Set());
    setEditedContacts({});
    setEditingContact(null);
    onClose();
  };

  const getValidationIcon = (contact: ContactValidationResult) => {
    if (!contact.isValid) return <ErrorIcon color="error" />;
    if (contact.missingVariables.length > 0) return <WarningIcon color="warning" />;
    if (contact.exists) return <WarningIcon color="info" />;
    return <CheckCircleIcon color="success" />;
  };

  const getValidationColor = (contact: ContactValidationResult) => {
    if (!contact.isValid) return 'error.light';
    if (contact.missingVariables.length > 0) return 'warning.light';
    if (contact.exists) return 'info.light';
    return 'success.light';
  };

  const filteredContacts = getFilteredContacts();
  const validContacts = filteredContacts.filter(contact => 
    contact.isValid && contact.missingVariables.length === 0
  );
  const selectedValidContacts = validContacts.filter(contact => 
    selectedContacts.has(contact.email)
  );

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Review and Edit Contacts</Typography>
            <Typography variant="body2" color="text.secondary">
              Review contacts, fix missing data, and select which ones to import
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showOnlyIssues}
                  onChange={(e) => setShowOnlyIssues(e.target.checked)}
                  size="small"
                />
              }
              label="Show only issues"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                />
              }
              label="Update existing"
            />
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert 
            severity={filteredContacts.some(c => !c.isValid || c.missingVariables.length > 0) ? 'warning' : 'success'}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">
              <strong>{filteredContacts.length}</strong> contacts shown • 
              <strong> {validContacts.length}</strong> valid • 
              <strong> {filteredContacts.filter(c => c.exists).length}</strong> already exist • 
              <strong> {selectedValidContacts.length}</strong> selected for import
            </Typography>
          </Alert>

          {template && template.variables.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Required template variables:
              </Typography>
              <Box sx={{ mt: 1 }}>
                {template.variables.map(variable => (
                  <Chip key={variable} label={variable} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                ))}
              </Box>
            </Alert>
          )}
        </Box>

        <Paper sx={{ maxHeight: 600, overflow: 'auto' }}>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedValidContacts.length > 0 && selectedValidContacts.length < validContacts.length}
                      checked={validContacts.length > 0 && selectedValidContacts.length === validContacts.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Template Variables</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Issues</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredContacts.map((contact) => (
                  <TableRow 
                    key={contact.email}
                    sx={{ 
                      backgroundColor: getValidationColor(contact),
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedContacts.has(contact.email)}
                        onChange={() => handleContactToggle(contact.email)}
                        disabled={!contact.isValid || contact.missingVariables.length > 0}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={contact.issues.join(', ') || 'Valid'}>
                        {getValidationIcon(contact)}
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {editingContact === contact.email ? (
                        <TextField
                          size="small"
                          value={contact.email}
                          disabled
                          sx={{ minWidth: 200 }}
                        />
                      ) : (
                        contact.email
                      )}
                    </TableCell>
                    <TableCell>
                      {editingContact === contact.email ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            size="small"
                            placeholder="First name"
                            value={contact.firstName || ''}
                            onChange={(e) => handleFieldChange(contact.email, 'firstName', e.target.value)}
                          />
                          <TextField
                            size="small"
                            placeholder="Last name"
                            value={contact.lastName || ''}
                            onChange={(e) => handleFieldChange(contact.email, 'lastName', e.target.value)}
                          />
                        </Box>
                      ) : (
                        [contact.firstName, contact.lastName].filter(Boolean).join(' ') || '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {editingContact === contact.email ? (
                        <TextField
                          size="small"
                          placeholder="Company"
                          value={contact.company || ''}
                          onChange={(e) => handleFieldChange(contact.email, 'company', e.target.value)}
                        />
                      ) : (
                        contact.company || '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {editingContact === contact.email ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
                          {template?.variables.map(variable => (
                            <TextField
                              key={variable}
                              size="small"
                              label={variable}
                              value={contact.variables[variable] || ''}
                              onChange={(e) => handleVariableChange(contact.email, variable, e.target.value)}
                              error={!contact.variables[variable]}
                              helperText={!contact.variables[variable] ? 'Required' : ''}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {template?.variables.map(variable => (
                            <Chip 
                              key={variable}
                              label={`${variable}: ${contact.variables[variable] || 'missing'}`}
                              size="small"
                              color={contact.variables[variable] ? 'default' : 'error'}
                              variant={contact.variables[variable] ? 'outlined' : 'filled'}
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.issues.length > 0 ? (
                        <Box>
                          {contact.issues.map((issue, i) => (
                            <Chip key={i} label={issue} size="small" color="error" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                        </Box>
                      ) : (
                        <Chip label="Valid" size="small" color="success" />
                      )}
                    </TableCell>
                    <TableCell>
                      {editingContact === contact.email ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleSaveContact(contact.email)}
                            color="primary"
                          >
                            <SaveIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={handleCancelEdit}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {(contact.missingVariables.length > 0 || !contact.firstName || !contact.lastName) && (
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditContact(contact)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              const newSelected = new Set(selectedContacts);
                              newSelected.delete(contact.email);
                              setSelectedContacts(newSelected);
                            }}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleConfirm}
          disabled={selectedValidContacts.length === 0}
        >
          Import {selectedValidContacts.length} Contacts
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedCSVReview;
